import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Logo from 'Assets/rakennusliitto-logo.svg';
import DropdownMenu, { DropdownProps } from 'Components/DropdownMenu';
import { Desktop, Mobile, Tablet } from 'Components/Layout/MediaQuery';
import LoginModal from 'Features/Auth/LoginModal';
import { genericActions, userLogout } from 'Features/Auth/authSlice';
import { genericActions as organizationGenericActions } from 'Features/Organization/organizationSlice';
import { RootState } from 'Store';
import {
  isAdmin,
  isHouseholdAdmin,
  isJobSeeker,
  isOrganizationAdmin,
} from 'Util/permissions';
import type { Location } from 'history';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, withRouter } from 'react-router-dom';
import styled from 'styled-components/macro';
import NavigationDropdown from './NavigationDropdown';

interface StyledLinkProps {
  isActive: boolean;
}
interface MobileMenuProps {
  open: boolean;
  height: Number;
}

const NavbarContainer = styled.div`
  max-height: 60px;
  min-height: 60px;
  width: 100%;
  user-select: none;
  position: fixed;
  top: 0;
  left: 0;
  background: #fff;
  z-index: 999;
`;
const NavbarContent = styled.div`
  max-height: 60px;
  min-height: 60px;
  width: 100%;
  padding: 10px;
  display: flex;
  align-items: center;
  img {
    height: 30px;
    width: 30px;
  }
  svg.hamburger {
    position: absolute;
    right: 15px;
    top: calc(50% - 10px);
    height: 20px;
    width: 20px;
    cursor: pointer;
  }
`;
const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  @media screen and ${(props) => props.theme.breakpoints.lg} {
    width: 190px;
  }
`;
const MenuItems = styled.div`
  flex-grow: 1;
  display: flex;
  a {
    font-family: acumin-pro;
    font-size: 16px;
    letter-spacing: 0;
    line-height: 19px;
    margin-right: 50px;
    text-decoration: none;
    &.outside-link {
      color: #252525;
    }
  }
`;
const MobileMenuItems = styled.div`
  background: #fff;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: ${(props: MobileMenuProps) =>
    props.open ? `${props.height}px` : '0px'};
  display: flex;
  flex-direction: column;
  transition: max-height 0.3s ease-in-out;
  overflow-y: hidden;
  a {
    font-family: acumin-pro;
    font-size: 16px;
    letter-spacing: 0;
    line-height: 19px;
    margin-right: 50px;
    text-decoration: none;
    padding: 10px 20px;
    &.outside-link {
      color: #252525;
    }
  }
`;
const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  padding: 10px 10px;
  &:last-of-type {
    padding-bottom: 20px;
  }
  @media screen and ${(props) => props.theme.breakpoints.lg} {
    &:last-of-type {
      padding-bottom: 10px;
    }
  }
`;
const LoginLink = styled.div`
  color: #5c5f63;
  font-family: acumin-pro;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 16px;
  cursor: ${(props: { cursor: string }) => props.cursor};
`;
const NavActionLink = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #5c5f63;
  font-family: acumin-pro;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 16px;
  cursor: pointer;
  white-space: nowrap;
  svg {
    margin: 0;
  }
  &:hover {
    color: #252525;
  }
`;
const SubMenuItems = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding-left: 60px;
  a {
    font-family: acumin-pro;
    font-weight: normal;
    font-size: 13px;
    letter-spacing: 0;
    line-height: 16px;
    text-decoration: none;
    padding: 8px 0px 8px 0px;
    &.outside-link {
      color: white;
    }
  }
`;
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-unused-vars */
const StyledLink = styled(({ isActive, ...rest }) => <Link {...rest} />)`
  ${(props: StyledLinkProps) =>
    props.isActive
      ? `
      font-weight: bold;
      color: #D73714;
  `
      : 'color: #252525;'}
`;
/* eslint-enable react/jsx-props-no-spreading */
/* eslint-enable @typescript-eslint/no-unused-vars */

const Navbar = ({ location }: { location: Location }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );
  const [mobileHeight, setMobileHeight] = React.useState<Number>(0);
  const [isMobileMenuOpen, setMobileMenuOpen] = React.useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = React.useState<boolean>(false);
  const wageMillNavigationDropdownOptions = [
    {
      title: t('households'),
      action: () => {
        window.location.href = 'https://palkkamylly.palkkaus.fi/household';
      },
    },
    {
      title: t('employees-members'),
      action: () => {
        history.push('/palkanmaksu');
      },
    },
    {
      title: t('companies'),
      action: () => {
        window.location.href = 'https://palkkamylly.palkkaus.fi/company';
      },
    },
  ];

  const jobMillNavigationDropdownOptions = [
    {
      title: t('households'),
      action: () => {
        history.push('/admin-panel');
      },
    },
    {
      title: t('companies-employers'),
      action: () => {
        history.push('/admin-panel');
      },
    },
    {
      title: t('employees-members'),
      action: () => {
        history.push('/jobs');
      },
    },
  ];

  const hasAdminRole =
    isAuthenticated &&
    user.groups &&
    (isAdmin(user.groups) ||
      (isOrganizationAdmin(user.groups) &&
        (user.is_employer || user.is_trainer)) ||
      (isHouseholdAdmin(user.groups) && user.is_household));

  const userOptions: Array<DropdownProps> = [];
  if (isAuthenticated && user.groups) {
    if (isJobSeeker(user.groups)) {
      userOptions.push({
        icon: 'user-circle',
        title: t('user-profile'),
        action: () => {
          history.push('/profile');
        },
      });
    }
    if (isAdmin(user.groups)) {
      userOptions.push({
        icon: 'briefcase',
        title: t('super-admin-panel'),
        action: () => {
          history.push('/super-admin-panel');
        },
      });
    }
    if (isOrganizationAdmin(user.groups) && user.is_employer) {
      userOptions.push({
        icon: 'briefcase',
        title: t('admin-panel'),
        action: () => {
          history.push('/admin-panel');
        },
      });
    }
    if (isOrganizationAdmin(user.groups) && user.is_trainer) {
      userOptions.push({
        icon: 'briefcase',
        title: t('education-panel'),
        action: () => {
          history.push('/education-panel');
        },
      });
    }
    if (isHouseholdAdmin(user.groups) && user.is_household) {
      userOptions.push({
        icon: 'briefcase',
        title: t('admin-panel'),
        action: () => {
          history.push('/admin-panel');
        },
      });
    }
    userOptions.push({
      icon: 'sign-out',
      title: t('log-out'),
      action: () => {
        dispatch(userLogout());
        dispatch(genericActions.ResetAction());
        dispatch(organizationGenericActions.ResetAction());
      },
    });
  }

  const renderMobileNav = () => (
    <>
      <MobileMenuItems
        ref={(el: HTMLDivElement) => {
          if (!el) {
            return;
          }

          if (mobileHeight !== el.scrollHeight) {
            setMobileHeight(el.scrollHeight);
          }
        }}
        open={isMobileMenuOpen}
        height={mobileHeight}
      >
        <StyledLink isActive={location.pathname === '/jobs'} to="/jobs">
          {t('job-mill')}
        </StyledLink>
        <StyledLink
          isActive={location.pathname === '/trainings'}
          to="/trainings"
        >
          {t('skill-mill')}
        </StyledLink>
        <StyledLink isActive={false} to="/palkanmaksu">
          {t('wage-payment')}
        </StyledLink>
        <StyledLink
          isActive={location.pathname === '/mylly/suuntamylly'}
          to="/mylly/suuntamylly"
        >
          {t('personal-assistance')}
        </StyledLink>
        {!isAuthenticated && (
          <SubMenuItems>
            <>
              <a href="https://palkkamylly.palkkaus.fi/household">
                {t('households')}
              </a>
              <StyledLink
                isActive={location.pathname === '/palkanmaksu'}
                to="/palkanmaksu"
              >
                {t('employees-members')}
              </StyledLink>
              <a href="https://palkkamylly.palkkaus.fi/company">
                {t('companies')}
              </a>
            </>
          </SubMenuItems>
        )}
        <a className="outside-link" href="https://rakennusliitto.fi">
          Rakennusliitto.fi
        </a>
        {isAuthenticated && user.groups && isAdmin(user.groups) && (
          <StyledLink isActive={false} to="/super-admin-panel">
            {t('super-admin-panel')}
          </StyledLink>
        )}
        {isAuthenticated &&
          user.groups &&
          isOrganizationAdmin(user.groups) &&
          user.is_employer && (
            <StyledLink isActive={false} to="/admin-panel">
              {t('admin-panel')}
            </StyledLink>
          )}
        {isAuthenticated &&
          user.groups &&
          isOrganizationAdmin(user.groups) &&
          user.is_trainer && (
            <StyledLink isActive={false} to="/education-panel">
              {t('education-panel')}
            </StyledLink>
          )}
        {isAuthenticated &&
          user.groups &&
          isHouseholdAdmin(user.groups) &&
          user.is_household && (
            <StyledLink isActive={false} to="/admin-panel">
              {t('admin-panel')}
            </StyledLink>
          )}
        <ActionsContainer>
          {isAuthenticated ? (
            <>
              <NavActionLink onClick={() => history.push('/profile')}>
                <FontAwesomeIcon icon={['far', 'user']} />
                {t('my-profile')}
              </NavActionLink>
              <NavActionLink
                onClick={() => {
                  dispatch(userLogout());
                  dispatch(genericActions.ResetAction());
                  dispatch(organizationGenericActions.ResetAction());
                }}
              >
                <FontAwesomeIcon icon={['far', 'sign-out']} />
                {t('log-out')}
              </NavActionLink>
            </>
          ) : (
            <LoginLink cursor="pointer" onClick={() => setShowLoginModal(true)}>
              {t('log-in')}
            </LoginLink>
          )}
        </ActionsContainer>
      </MobileMenuItems>
      <FontAwesomeIcon
        onClick={() => setMobileMenuOpen((state) => !state)}
        icon={['far', 'bars']}
        className="hamburger"
      />
    </>
  );
  return (
    <NavbarContainer>
      <NavbarContent>
        <LogoContainer>
          <img src={Logo} alt="Rakennusliitto logo" />
        </LogoContainer>
        <Desktop>
          <>
            <MenuItems>
              <NavigationDropdown
                options={jobMillNavigationDropdownOptions}
                dropdownLinkTitle="job-mill"
              />
              <StyledLink
                isActive={location.pathname === '/trainings'}
                to="/trainings"
              >
                {t('skill-mill')}
              </StyledLink>
              {isAuthenticated ? (
                <StyledLink isActive={false} to="/palkanmaksu">
                  {t('wage-payment')}
                </StyledLink>
              ) : (
                <NavigationDropdown
                  options={wageMillNavigationDropdownOptions}
                  dropdownLinkTitle="wage-payment"
                />
              )}
              <StyledLink
                isActive={location.pathname === '/mylly/suuntamylly'}
                to="/mylly/suuntamylly"
              >
                {t('personal-assistance')}
              </StyledLink>
              <a className="outside-link" href="https://rakennusliitto.fi">
                Rakennusliitto.fi
              </a>
            </MenuItems>
            <ActionsContainer>
              {isAuthenticated ? (
                hasAdminRole ? (
                  <>
                    <LoginLink cursor="default">
                      {t('hey')}, {user.first_name}
                    </LoginLink>
                    <DropdownMenu
                      options={userOptions}
                      menuPosition="leftBottom"
                      objectId={0}
                    />
                  </>
                ) : (
                  <>
                    <NavActionLink onClick={() => history.push('/profile')}>
                      <FontAwesomeIcon icon={['far', 'user']} />
                      {t('my-profile')}
                    </NavActionLink>
                    <NavActionLink
                      onClick={() => {
                        dispatch(userLogout());
                        dispatch(genericActions.ResetAction());
                      }}
                    >
                      <FontAwesomeIcon icon={['far', 'sign-out']} />
                      {t('log-out')}
                    </NavActionLink>
                  </>
                )
              ) : (
                <LoginLink
                  cursor="pointer"
                  onClick={() => setShowLoginModal(true)}
                >
                  {t('log-in')}
                </LoginLink>
              )}
            </ActionsContainer>
          </>
        </Desktop>
        <Tablet>{renderMobileNav()}</Tablet>
        <Mobile>{renderMobileNav()}</Mobile>
        <LoginModal
          modalOpen={showLoginModal}
          closeModal={() => setShowLoginModal(false)}
        />
      </NavbarContent>
    </NavbarContainer>
  );
};

export default withRouter(Navbar);
